import React, { useState } from 'react';
import { Box, Stack, Typography, Avatar, InputBase, IconButton, Tooltip, Badge, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { useNavigate } from 'react-router-dom';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import { EmptyState } from '../uiKit';
import ChatLoader from './ChatLoader';
import { avatarToneFor, initialsFrom, formatMessageTime } from './chatHelpers';

export default function ConversationList({
    groups, loading, selectedGroupId, onSelect, search, onSearchChange,
    canCreateGroup, onCreateGroup, isMasterAdmin, hidden, typingByGroupId,
}) {
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = useState(null);

    if (hidden) return null;

    const q = search.trim().toLowerCase();
    const filtered = q ? groups.filter((g) => g.name.toLowerCase().includes(q)) : groups;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', p: 1.6, borderBottom: '1px solid #EEF0F6' }}>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A', flex: 1 }}>Chats</Typography>
                {(canCreateGroup || isMasterAdmin) && (
                    <>
                        <Tooltip arrow title="More">
                            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ width: 34, height: 34, color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                                <MoreVertRoundedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={() => setMenuAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{ paper: { sx: { borderRadius: '9px', border: '1px solid #EEF0F6', boxShadow: '0 10px 30px rgba(16,24,40,0.12)', minWidth: 190 } } }}
                        >
                            {(canCreateGroup || isMasterAdmin) && (
                                <MenuItem onClick={() => { setMenuAnchor(null); onCreateGroup(); }} sx={{ fontSize: 13.5, py: 1 }}>
                                    <ListItemIcon><GroupAddRoundedIcon sx={{ fontSize: 19, color: PRIMARY }} /></ListItemIcon>
                                    <ListItemText>New group</ListItemText>
                                </MenuItem>
                            )}
                            {isMasterAdmin && (
                                <MenuItem onClick={() => { setMenuAnchor(null); navigate('/dashboard/chat/settings'); }} sx={{ fontSize: 13.5, py: 1 }}>
                                    <ListItemIcon><TuneRoundedIcon sx={{ fontSize: 19, color: PRIMARY }} /></ListItemIcon>
                                    <ListItemText>Group chat settings</ListItemText>
                                </MenuItem>
                            )}
                        </Menu>
                    </>
                )}
            </Stack>

            <Box sx={{ px: 1.6, py: 1.2 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '9px', px: 1.4, height: 38 }}>
                    <SearchRoundedIcon sx={{ fontSize: 17, color: '#98A0AE' }} />
                    <InputBase placeholder="Search chats…" value={search} onChange={(e) => onSearchChange(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                </Stack>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ChatLoader size={40} />
                    </Box>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon={GroupsRoundedIcon}
                        title={q ? 'No chats match your search' : 'No conversations yet'}
                        hint={canCreateGroup ? 'Start a new group to begin chatting.' : 'Groups you\'re added to will show up here.'}
                    />
                ) : (
                    filtered.map((g) => {
                        const active = g.groupId === selectedGroupId;
                        const tone = avatarToneFor(g.name);
                        const typingName = typingByGroupId?.[g.groupId];
                        return (
                            <Stack
                                key={g.groupId}
                                direction="row"
                                spacing={1.2}
                                onClick={() => onSelect(g.groupId)}
                                sx={{
                                    alignItems: 'flex-start', px: 1.6, py: 1.3, cursor: 'pointer',
                                    borderBottom: '1px solid #F4F5F9',
                                    bgcolor: active ? PRIMARY_LIGHT : '#fff',
                                    transition: 'background-color .15s',
                                    '&:hover': { bgcolor: active ? PRIMARY_LIGHT : '#F8F8FC' },
                                }}
                            >
                                {g.iconUrl ? (
                                    <Avatar src={g.iconUrl} sx={{ width: 42, height: 42, flexShrink: 0 }} />
                                ) : (
                                    <Avatar sx={{ width: 42, height: 42, flexShrink: 0, ...tone, fontSize: 14, fontWeight: 800 }}>{initialsFrom(g.name)}</Avatar>
                                )}
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: g.unreadCount ? 800 : 700, color: '#0F172A', flex: 1, minWidth: 0 }} noWrap>{g.name}</Typography>
                                        {g.isMuted && <VolumeOffRoundedIcon sx={{ fontSize: 15, color: '#B0B6C3' }} />}
                                        <Typography sx={{ fontSize: 10.5, color: '#98A0AE', flexShrink: 0 }}>{formatMessageTime(g.lastMessageOn)}</Typography>
                                    </Stack>
                                    <Stack direction="row" sx={{ alignItems: 'center', mt: 0.3 }}>
                                        {typingName ? (
                                            <Typography sx={{ fontSize: 12, color: '#16A34A', fontWeight: 700, fontStyle: 'italic', flex: 1, minWidth: 0 }} noWrap>
                                                {typingName} is typing…
                                            </Typography>
                                        ) : (
                                            <Typography sx={{ fontSize: 12, color: '#64748B', flex: 1, minWidth: 0 }} noWrap>
                                                {g.lastMessageSenderName ? `${g.lastMessageSenderName}: ` : ''}{g.lastMessageText || 'No messages yet'}
                                            </Typography>
                                        )}
                                        {g.unreadCount > 0 && (
                                            <Badge
                                                badgeContent={g.unreadCount > 99 ? '99+' : g.unreadCount}
                                                sx={{ ml: 1, '& .MuiBadge-badge': { position: 'static', transform: 'none', bgcolor: PRIMARY, color: '#fff', fontSize: 10, fontWeight: 800, height: 18, minWidth: 18, borderRadius: '9px' } }}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        );
                    })
                )}
            </Box>
        </Box>
    );
}
